import { GetApp, Settings, VerifiedUser, SupervisorAccount, Add } from "@material-ui/icons";
import React, { useEffect, useState, useMemo } from "react";
import { Box, Grid, Divider, Tooltip, IconButton, Button, makeStyles } from "@material-ui/core";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import select from "../../../utils/select";
import { rowRender } from "../utils/render";

import ActionButton from "../utils/ActionButton";

import { KPTable } from "../../../components/KPTComponents";
import InstitutionalProjectModal from "../InstitutionalProjectModal";
import { INSTITUTIONAL_PROJECT } from "../../../constants/selectOptions";
import { AcademicRankAbbrv, DegreeAbbrv } from "../../../utils/abbreviation";
import { INTERNAL_RESEARCH_STATUS_LABEL } from "../../../constants/internalResearchStatus";
import ROUTER from "../../../constants/router";
// Refactor 2026-05-21: filter "Sắp đến hạn / Quá hạn" — chip bar +
// dropdown drill-down + cột "Sắp hạn" có badge + tooltip chi tiết.
import { DEADLINE_KINDS_IR } from "../../../constants/deadlineKinds";
import { computeDeadlines, rowMatchesFilter } from "../../../utils/deadlineCompute";
import useDueDate from "../../../utils/useDueDate";
import { DeadlineFilterBar, DeadlineBadge } from "../../../components/DeadlineFilter";

const useStyles = makeStyles(() => ({
  tableRoot: {
    "& tbody tr": { height: 1 },
    "& tbody tr td": { height: "inherit" },
  },
}));

const sortFunc = (a, b) => {
  if (a.researchRole === "main") return -1;
  if (b.researchRole === "main") return 1;
  return a.id - b.id;
};

function InstitutionalProjectTable(props) {
  const styles = useStyles();
  const history = useHistory();

  const { type, year } = props;

  const [open, setOpen] = useState(null);
  const [detail, setDetail] = useState(null);
  const [datasource, setDatasource] = useState([]);

  const profileList = useSelector((state) => select(state, "profileReducer", "list")) || [];
  const list = useSelector((state) => select(state, "internalResearchReducer", "list")) || [];
  const isFetching = useSelector((state) => select(state, "internalResearchReducer", "isFetching"));
  // Refactor 2026-06-02: chỉ sudo-admin + admin Phòng QLKH được xoá đề tài.
  const userRole = useSelector((state) => select(state, "userReducer", "role"));
  const canDelete = userRole === "sudo-admin" || type === "admin";

  // Refactor 2026-05-21: filter sắp đến hạn / quá hạn (IR).
  const [chip, setChip] = useState("all");
  const [kindKey, setKindKey] = useState("all");
  // reloadKey từ parent (bumped khi user click Replay) → refetch YearlyDueDate.
  const { dueDate } = useDueDate("internal", year, props.reloadKey);

  useEffect(() => {
    const fullRows = list.map((e) => {
      // Parse status và tạo bản copy để tránh mutate dữ liệu gốc
      let statusArray;
      if (typeof e?.status === "string") {
        statusArray = e.status.split(",");
      } else if (Array.isArray(e?.status)) {
        statusArray = [...e.status]; // Create a shallow copy
      } else {
        statusArray = [];
      }

      return {
        ...e,
        participants: e?.participants?.map((participant) => {
          const profile = profileList.find((p) => participant.profileId === p.id);
          if (!profile) return participant;
          return {
            ...participant,
            degree: profile.degree,
            academicRank: profile.academicRank,
          };
        }),
        status: statusArray,
      };
    });
    const _datasource = fullRows.map((row) => {
      // Refactor 2026-05-15: rowRender mới nhận (status, type, row) thay vì
      // (status, type, infoPassed). Truyền nguyên row để rowRender đọc được
      // durationMonths + extendedUntilDate + councilAssignments + progressReport.
      const { status, buttons } = rowRender(row.status, type, row);
      // Refactor 2026-05-21: compute deadlines cho row.
      const _deadlines = computeDeadlines(
        { ...row, rawStatus: row.status, status },
        dueDate,
        DEADLINE_KINDS_IR
      );
      // Refactor 2026-06-02: append "delete" cho role được phép xoá.
      const finalButtons =
        canDelete && !buttons.includes("delete") ? [...buttons, "delete"] : buttons;

      return {
        ...row,
        rawStatus: row.status,
        status,
        actions: finalButtons,
        participantRoles: row.participants,
        _deadlines,
      };
    });

    if (list) setDatasource(_datasource);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, dueDate, canDelete]);

  // Refactor 2026-05-21: filter rows theo chip + kindKey + tính count.
  const filteredDatasource = useMemo(() => {
    if (chip === "all" && kindKey === "all") return datasource;
    return datasource.filter((row) =>
      rowMatchesFilter(row._deadlines || [], chip, kindKey)
    );
  }, [datasource, chip, kindKey]);

  const counts = useMemo(() => {
    const c = { all: datasource.length, overdue: 0, soon7: 0, soon30: 0 };
    for (const row of datasource) {
      const dl = row._deadlines || [];
      if (rowMatchesFilter(dl, "overdue", kindKey)) c.overdue++;
      if (rowMatchesFilter(dl, "soon7", kindKey)) c.soon7++;
      if (rowMatchesFilter(dl, "soon30", kindKey)) c.soon30++;
    }
    return c;
  }, [datasource, kindKey]);

  return (
    // Layout 2026-09-11: flex column cao 100% — thanh filter giữ chiều cao tự
    // nhiên, KPTable (fillHeight) chiếm phần còn lại nên paging luôn trong khung.
    <Grid
      item
      xs={12}
      style={{
        position: "relative",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <Box flexShrink={0}>
        <DeadlineFilterBar
          chip={chip}
          setChip={setChip}
          kindKey={kindKey}
          setKindKey={setKindKey}
          kinds={DEADLINE_KINDS_IR}
          counts={counts}
        />
      </Box>
      <KPTable
        fillHeight
        tableProps={{
          classes: {
            root: styles.tableRoot,
          },
        }}
        // totalData={rows.length}
        // rowsPerPage={50}
        border={true}
        // Refactor 2026-05-21: bật localPaging để TablePagination thực sự slice
        // rows. Nếu không, mọi rows đẩy ra DOM cùng lúc + click trang vô tác dụng.
        localPaging={true}
        loading={isFetching}
        filterObj={props.filterObj}
        columns={[
          {
            title: <b>Mã đề tài</b>,
            key: "researchCode",
            // 2026-07-31: mã tự sinh "ĐTCS_<khoa>_xx.yy" dài, không wordBreak
            // nên tràn (bleed) sang cột Tên đề tài kế bên — thêm wordBreak +
            // tăng nhẹ width 100→110 để mã wrap gọn trong ô.
            width: 110,
            searchable: true,
            onCell: (row) => ({ rowSpan: row.rowSpan }),
            render: (value) =>
              value ? <span style={{ wordBreak: "break-word" }}>{value}</span> : "N/A",
          },
          {
            // Sàn 240px qua `max()` — tránh shrink dưới ngưỡng khi sidebar
            // mở + viewport hẹp. Tổng fixed cols (Mã + Cán bộ + Vai trò +
            // Trạng thái + Tác vụ) = 740 nên 2 cột tên chia phần còn lại.
            title: <b>Tên đề tài tiếng Việt</b>,
            key: "vietnameseName",
            width: "max(240px, calc((100% - 740px) / 2))",
            searchable: true,
            onCell: (row) => ({ rowSpan: row.rowSpan }),
          },
          {
            title: <b>Tên đề tài tiếng Anh</b>,
            key: "englishName",
            width: "max(240px, calc((100% - 740px) / 2))",
            searchable: true,
            onCell: (row) => ({ rowSpan: row.rowSpan }),
          },
          {
            title: <b>Cán bộ tham gia</b>,
            key: "participants",
            width: 200,
            render: (value, row) => {
              return (
                <Box margin="-6px -16px" width="calc(100% + 32px)" height="calc(100% + 12px)">
                  {value?.sort(sortFunc)?.map((e, id) => (
                    <Box
                      key={`participant-${row.id}-${id}`}
                      height={`calc(${100 / value?.length}% - ${id === 0 ? 0 : 1}px)`}
                      borderTop={id !== 0 ? "solid 1px #e0e0e0" : null}
                      display="flex"
                      alignItems="center"
                      padding="0 16px"
                      minHeight={28}
                      fontWeight={e.researchRole === "main" ? "bold" : "auto"}
                    >
                      {`${AcademicRankAbbrv(e?.academicRank)}
                      ${DegreeAbbrv(e?.degree)}
                      ${e?.name}`}
                    </Box>
                  ))}
                </Box>
              );
            },
          },
          {
            title: <b>Vai trò</b>,
            key: "participantRoles",
            align: "right",
            width: 100,
            render: (value, row) => {
              return (
                <Box margin="-6px -16px" width="calc(100% + 32px)" height="calc(100% + 12px)">
                  {value?.sort(sortFunc)?.map((e, id) => (
                    <Box
                      key={`role-${row.id}-${id}`}
                      height={`calc(${100 / value?.length}% - ${id === 0 ? 0 : 1}px)`}
                      borderTop={id !== 0 ? "solid 1px #e0e0e0" : null}
                      display="flex"
                      alignItems="center"
                      justifyContent="flex-end"
                      padding="0 16px"
                      minHeight={28}
                      width="calc(100% - 32px)"
                      fontWeight={e.researchRole === "main" ? "bold" : "auto"}
                    >
                      {INSTITUTIONAL_PROJECT.roleOptions.find((elm) => elm.value === e.researchRole)?.title}
                    </Box>
                  ))}
                </Box>
              );
            },
          },
          {
            title: <b>Trạng thái</b>,
            key: "status",
            align: "center",
            width: 226,
            filterable: true,
            // Refactor 2026-05-15: filter sang 30 status mới (xem
            // constants/internalResearchStatus.js). Tự sinh từ enum để khỏi
            // miss / lệch label.
            filters: Object.entries(INTERNAL_RESEARCH_STATUS_LABEL).map(
              ([value, text]) => ({ text, value })
            ),
            onFilter: (value, record) => {
              if (!value?.rawStatus) return false;
              return value.rawStatus.some((status) => record[status] === true);
            },
            render: (value, row) => (
              <>
                {value.map((e, index) => (
                  <Box
                    key={`status-${row.id}-${index}`}
                    bgcolor={`${e.bgcolor}16`}
                    border={`solid 1px ${e.bgcolor}`}
                    color={e.bgcolor}
                    fontSize={12}
                    borderRadius={16}
                    padding="2px 0"
                    width="calc(100% - 32px)"
                    margin="auto"
                    marginTop={index !== 0 ? 1 : 0}
                    textAlign="center"
                  >
                    {e.label}
                  </Box>
                ))}
              </>
            ),
            onCell: (row) => ({ rowSpan: row.rowSpan }),
          },
          {
            // Refactor 2026-05-21: cột deadline IR.
            title: <b>Sắp hạn</b>,
            key: "_deadlines",
            width: 110,
            align: "center",
            onCell: (row) => ({ rowSpan: row.rowSpan }),
            render: (value) => (
              <DeadlineBadge deadlines={value || []} kindKey={kindKey} />
            ),
          },
          {
            title: <b>Tác vụ</b>,
            key: "actions",
            align: "right",
            width: 104,
            onCell: (row) => ({ rowSpan: row.rowSpan }),
            render: (value, row) => (
              <ActionButton
                parent={row.parentResearch}
                row={row}
                actions={value}
                setOpen={setOpen}
                setDetail={setDetail}
              />
            ),
          },
        ]}
        rows={filteredDatasource}
      />

      {/* Refactor 2026-05-15: thêm nút "Thêm đề tài" cho personal (CN) để khởi
          tạo registration_draft thẳng từ list. Cũng giữ nút Export/Setting cho admin/mod. */}
      <Box bottom={6} marginLeft={0} position="absolute" display="flex">
        {props.type === "personal" && (
          <Button
            variant="contained" color="primary"
            startIcon={<Add />}
            onClick={() => setOpen({ insert: true })}
            style={{ marginRight: 8 }}
          >
            Thêm đề tài
          </Button>
        )}
        {(props.type === "admin" || props.type === "mod") && (
          <>
            <IconButton onClick={() => setOpen({ export: true })}>
              <Tooltip title="Xuất file báo cáo">
                <GetApp />
              </Tooltip>
            </IconButton>
            {/* Refactor 2026-05-20: Hội đồng tiểu ban + Check trùng + Cấu hình
                chuyển từ cấp Khoa (mod) → cấp QLKH (admin) — đúng phân quyền
                nghiệp vụ (QLKH quản lý template HĐ + cấu hình deadline). */}
            {props.type === "admin" && (
              <>
                <IconButton onClick={() => history.push(`${ROUTER.NCKH_CO_SO}/council`)}>
                  <Tooltip title="Hội đồng các tiểu ban">
                    <SupervisorAccount />
                  </Tooltip>
                </IconButton>
                <Divider orientation="vertical" style={{ height: 32, margin: "8px" }} />
                <IconButton>
                  <Tooltip title="Check trùng đề tài">
                    <VerifiedUser />
                  </Tooltip>
                </IconButton>
                <IconButton onClick={() => setOpen({ setting: true })}>
                  <Tooltip title="Cấu hình">
                    <Settings />
                  </Tooltip>
                </IconButton>
              </>
            )}
          </>
        )}
      </Box>

      <InstitutionalProjectModal open={open} setOpen={setOpen} type={type} year={year} detail={detail} />
    </Grid>
  );
}

export default InstitutionalProjectTable;

